import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {DocumentsApiService} from '../../../services/api/documents-api.service';
import {AlertService} from '../../../services/notify/alert.service';
import {ModalService} from '../../../services/modal/modal.service';
import {HelpersService} from '../../../services/helpers.service';
import {MemberModel} from '../../../models/user.model';
import {MemberService} from '../../../services/member.service';
import {FileModel, SignersOrVisorsModel} from '../../../models/file.model';
import {IExtendedStructureDepartmentModel, StructureDepartmentModel} from '../../../models/department.model';
import {DepartmentsService} from '../../../services/api/departments.service';
import {catchError, EMPTY, filter, Observable, of, Subscription, switchMap, tap} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import { AppContentDialogComponentMT } from '../../material/content-dialog/app-content-dialog.component';
import { ContractAvatarsService } from '../../../modules/contracts/services/contract-avatar.service';
import { DialogsMTService } from '../../../services/modal/dialogs-mt.service';

@Component({
    selector: 'app-visors',
    templateUrl: './visors.component.html',
    styleUrls: ['./visors.component.scss']
})
export class VisorsComponent implements OnInit, OnChanges {
    @Input() fileKey!: string;
    @Input() collectionId!: number;
    @Input() public fileModel!: FileModel;
    @Input() public typeObj: 'file' | 'collection' = 'file';
    @Input() public editPermission!: boolean;

    @Output() public updateEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() public updateApproveEvent: EventEmitter<any> = new EventEmitter<any>();

    public visors!: SignersOrVisorsModel[];
    public isLockButton = false;
    public allMembers: MemberModel[] = [];
    public currentMember!: MemberModel;
    public currentVisor!: SignersOrVisorsModel|undefined;

    approveType: 'any' | 'all'| null = null;

    allDepartments: StructureDepartmentModel[] = [];
    selectedDepartmentWithDivisions!: StructureDepartmentModel;
    selectedDivisionId!: number;
    divisionSlider: {
        [key: number]: boolean
    } = {};

    private subs: Subscription = new Subscription();
    private contentDialogRef!: MatDialogRef<any>;
    
    constructor(
        private documentApi: DocumentsApiService,
        private alert: AlertService,
        private memberService: MemberService,
        private departmentsApi: DepartmentsService,
        public modalService: ModalService,
        public helper: HelpersService,
        public translate: TranslateService,
        private dialog: MatDialog,
        private contractAvatarsService: ContractAvatarsService,
        private dialogsMTService: DialogsMTService,
        private cds: ChangeDetectorRef,
    ) {
    }
    ngOnDestroy(): void {
        this.subs.unsubscribe()
    }
    openDialog(content: TemplateRef<any>,minWidth?:number,minHeight?:number) {
      this.contentDialogRef = this.dialog.open(AppContentDialogComponentMT,{panelClass:'visors-dialog',minHeight:minHeight,minWidth:minWidth})
      this.contentDialogRef.componentInstance.templateRef = content
    }
    ngOnInit(): void {
        this.setSubscriptions()
        if (this.fileKey) {
            this.getFileVisors();
        }
        this.memberService.getAllMembers().then(res => {
            this.allMembers = res;
            this.allMembers.forEach(el=>el.computed_name = this.helper.fullName(el)||el.email)
            this.allMembers.sort((a,b)=>(a.computed_name||'')>(b.computed_name||'')? 1 : -1)
        });

    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.fileKey) {
            this.getFileVisors();
        }
    }
  
    setSubscriptions() {
        this.subs.add(this.contractAvatarsService.bSubAvatarsReady.asObservable().subscribe(()=>this.cds.detectChanges()))
    }

    getFileVisors(): void {
        this.documentApi.getFileVisors(this.fileKey).subscribe({
            next: res => {
                this.visors = res.approvers;
                this.contractAvatarsService.getAvatars(res.approvers.map(el=>el.user.guid)).subscribe()
                this.allMembers = this.allMembers.filter(x => {
                    if (!this.visors.find((visor) => visor.user.guid === x.guid)) {
                        return x;
                    }
                });

                this.checkCurrentVisor();
                this.cds.detectChanges()
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    checkCurrentVisor(): void {
        if (!this.currentMember) {
            this.memberService.getCurrentMember().then(res => {
                this.currentMember = res;
                this.currentVisor = this.visors.find(visor => visor.user.guid === res.guid);
            });
        } else {
            this.currentVisor = this.visors.find(visor => visor.user.guid === this.currentMember.guid);
        }
    }

    addVisor(memberGuid: string): void {
        this.addVisorRequest(memberGuid).subscribe({
            next: () => {
               this.contractAvatarsService.getAvatars([memberGuid]).subscribe()
                if (this.approveType) {
                    this.approveType = null;
                }
                this.contentDialogRef.close();
                if (this.fileModel) {
                    this.getFileVisors();
                }
                this.updateEvent.emit();
                this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'nd_color');
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    addVisorRequest(memberGuid: string): Observable<any> {
        if (this.typeObj === 'file') {
            return this.documentApi.addVisorToFile(this.fileKey, memberGuid, {type_approve: this.approveType});
        } else {
            return this.documentApi.addVisorToCollection(this.collectionId, memberGuid, {type_approve_default: this.approveType});
        }
    }

    getAllDepartments(): Observable<IExtendedStructureDepartmentModel> {
			return this.departmentsApi.getStructureDepartments().pipe(
				tap(res=>this.allDepartments = res.departments.sort((a,b)=>(a.department_name||'')>(b.department_name||'')? 1 : -1))
			)
    }

    openVisorsModal(content: TemplateRef<any>): void {
        of(this.allDepartments.length>0)
        .pipe(switchMap(res=>res? of(res) : this.getAllDepartments()))
				.subscribe(()=>this.openDialog(content,500))
    }

    addVisorGroup(): void {
        const data: {
            type_obj: 'file' | 'collection';
            key_obj: string | number;
            division_id: number,
            type_approve_default: 'all' | 'any' | null
        } = {
            type_obj: this.typeObj,
            key_obj: this.fileKey ? this.fileKey : this.collectionId,
            division_id: this.selectedDivisionId,
            type_approve_default: this.approveType ? this.approveType : this.fileModel.type_approve
        };

        this.documentApi.addVisorGroupToFile(data).subscribe({
            next: () => {
                if (this.approveType) {
                    this.approveType = null;
                }
                this.contentDialogRef.close();
                if (this.fileModel) {
                    this.getFileVisors();
                }
                this.updateEvent.emit();
                this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'nd_color');
            }, error: err => {
                this.alert.setAlert(err.message, 'blue');
            }
        });
    }

    deleteVisor(visor: SignersOrVisorsModel): void {
        this.dialogsMTService.confirmDialog({actionHeader: 'CONFIRM.HEADERS.DELETE',actionBody: 'CONFIRM.TEXT.DELETE_VISOR' })
        .pipe(
            filter(res => res?.isConfirmed),
            switchMap(()=> this.documentApi.deleteVisorFromFile(this.fileKey, visor.user.guid).pipe(
            catchError(e => {
                this.alert.setAlert(e.message, 'blue');
                return EMPTY
                }))
            ),
            catchError(() => {
                            return EMPTY
            }))
        .subscribe(data=>{
            this.getFileVisors();
            this.updateEvent.emit();
            this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'nd_color');
        })
/*         const modalSettings = {
            modalHeader: this.translate.instant('CONFIRM.HEADERS.DELETE'),
            modalBody: this.translate.instant('CONFIRM.TEXT.DELETE_VISOR'),
            buttonSubmit: this.translate.instant('CONFIRM.BUTTONS.YES'),
            buttonClose: this.translate.instant('CONFIRM.BUTTONS.NO'),
        };
        const modal = this.modalService.createFromSettings(modalSettings);
        modal.result.then((data:any) => {
            if (data) {
                this.documentApi.deleteVisorFromFile(this.fileKey, visor.user.guid).subscribe({
                    next: () => {
                        this.getFileVisors();
                        this.updateEvent.emit();
                        this.alert.setAlert(this.translate.instant('ALERTS.SUCCESS'), 'green');
                    }, error: err => {
                        this.alert.setAlert(err.message, 'blue');
                    }
                });
            }
        }); */
    }
		approveDocument() {
			const isSign = true;
			this.dialogsMTService.openPinDialog(isSign,this.translate.instant('MODALS.HEADERS.APPLY'))
			.pipe(
				filter(pin=>pin?.pin != null),
				switchMap(pin =>  this.documentApi.applyDocument(this.fileKey, {PIN_code:pin.pin.toString()}).pipe(
					catchError(err=>{
						this.alert.setAlert(err.message, 'blue');
						return EMPTY
					})
				)),
				catchError(() => {
						return EMPTY;
				}))
			.subscribe(res=>{
				console.log('res',res )
				this.alert.setAlert(res.message, 'nd_color');
				this.updateApproveEvent.emit();
				this.getFileVisors();
			})
		}
}